import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import api from './api';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import TraderAnalysis from './components/TraderAnalysis';
import ConsequencesReport from './components/ConsequencesReport';
import TradeJournal from './components/TradeJournal'; 
import Chat from './components/Chat'; // NEW: Chat component
import Profile from './components/Profile'; // NEW: Profile component
import Loading from './components/Loading';
import InstallPrompt from './components/InstallPrompt'; // NEW: Install prompt for PWA

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    success: { main: '#4caf50' },
    error: { main: '#f44336' },
    warning: { main: '#ff9800' },
    background: { default: '#f5f5f5' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: 'none' } },
    },
  },
});

// Auth Context for better state management
const AuthContext = React.createContext({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [initialized, setInitialized] = React.useState(false);

  const checkAuth = React.useCallback(async (retryCount = 0) => {
    const token = localStorage.getItem('accessToken');
    console.log('🔍 AuthProvider: Checking auth, token exists:', !!token);
    
    if (!token) {
      console.log('🔍 AuthProvider: No token found');
      setUser(null);
      setLoading(false);
      setInitialized(true);
      return null;
    }

    try {
      console.log('🔍 AuthProvider: Fetching user data');
      const response = await api.get('/api/auth/user');
      console.log('✅ AuthProvider: User data received:', response.data);
      
      // Ensure user has required fields
      if (!response.data.role) {
        console.error('❌ AuthProvider: User missing role field');
        throw new Error('User role not defined');
      }
      
      setUser(response.data);
      setLoading(false);
      setInitialized(true);
      return response.data;
    } catch (error) {
      console.error('❌ AuthProvider: Auth check failed:', error);
      
      // Handle rate limiting with exponential backoff
      if (error.response?.status === 429 && retryCount < 3) {
        const backoffDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`🔄 AuthProvider: Rate limited, retrying in ${backoffDelay}ms (attempt ${retryCount + 1})`);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return checkAuth(retryCount + 1);
      }
      
      // For 429 errors after max retries, don't clear tokens immediately
      if (error.response?.status === 429) {
        console.warn('⚠️ AuthProvider: Rate limit exceeded, keeping tokens but setting user to null temporarily');
        setUser(null);
        setLoading(false);
        setInitialized(true);
        return null;
      }
      
      // Only clear tokens for non-rate-limiting auth failures
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('🔒 AuthProvider: Authentication failed, clearing tokens');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
      
      setUser(null);
      setLoading(false);
      setInitialized(true);
      return null;
    }
  }, []);

  React.useEffect(() => {
    console.log('🔍 AuthProvider: Initial auth check');
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, loading, initialized, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, initialized } = useAuth();
  const location = useLocation();
  
  console.log('🔐 ProtectedRoute:', {
    path: location.pathname,
    user: user?.email,
    role: user?.role,
    loading,
    initialized
  });

  if (loading || !initialized) {
    console.log('🔐 ProtectedRoute: Still loading...');
    return <Loading />;
  }

  if (!user) {
    console.log('🔐 ProtectedRoute: No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('🔐 ProtectedRoute: Access granted');
  return children;
};

// Super Admin Protected Route component
const SuperAdminProtectedRoute = ({ children }) => {
  const { user, loading, initialized } = useAuth();
  const location = useLocation();
  
  console.log('👑 SuperAdminProtectedRoute:', {
    path: location.pathname,
    user: user?.email,
    role: user?.role,
    loading,
    initialized
  });

  if (loading || !initialized) {
    console.log('👑 SuperAdminProtectedRoute: Still loading...');
    return <Loading />;
  }

  if (!user) {
    console.log('👑 SuperAdminProtectedRoute: No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'superadmin') {
    console.log('👑 SuperAdminProtectedRoute: Not superadmin, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('👑 SuperAdminProtectedRoute: Superadmin access granted');
  return children;
};

// Role-Based Redirect component for the root path
const RoleBasedRedirect = () => {
  const { user, loading, initialized } = useAuth();
  const navigate = useNavigate();
  
  console.log('🎯 RoleBasedRedirect:', {
    user: user?.email,
    role: user?.role,
    loading,
    initialized
  });

  React.useEffect(() => {
    if (initialized && !loading) {
      if (!user) {
        console.log('🎯 RoleBasedRedirect: No user, going to login');
        navigate('/login', { replace: true });
      } else {
        const targetRoute = user.role === 'superadmin' ? '/superadmin' : '/dashboard';
        console.log('🎯 RoleBasedRedirect: Redirecting to:', targetRoute);
        navigate(targetRoute, { replace: true });
      }
    }
  }, [user, loading, initialized, navigate]);

  if (loading || !initialized) {
    return <Loading />;
  }

  // This should rarely be reached due to useEffect navigation
  return null;
};



// Login wrapper that handles post-login navigation
const LoginWrapper = () => {
  const { user, initialized, checkAuth } = useAuth();
  const navigate = useNavigate();
  
  React.useEffect(() => {
    if (initialized && user) {
      const targetRoute = user.role === 'superadmin' ? '/superadmin' : '/dashboard';
      console.log('🔑 LoginWrapper: User already logged in, redirecting to:', targetRoute);
      navigate(targetRoute, { replace: true });
    }
  }, [user, initialized, navigate]);

  if (!initialized) {
    return <Loading />;
  }

  if (user) {
    return null; // Will redirect via useEffect
  }

  return <Login onLoginSuccess={checkAuth} />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Routes>
              <Route path="/login" element={<LoginWrapper />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis"
                element={
                  <ProtectedRoute>
                    <TraderAnalysis />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/superadmin"
                element={
                  <SuperAdminProtectedRoute>
                    <SuperAdminDashboard />
                  </SuperAdminProtectedRoute>
                }
              />
              <Route
                path="/consequences-report/:date"
                element={
                  <ProtectedRoute>
                    <ConsequencesReport />
                  </ProtectedRoute>
                }
              />
              <Route
                  path="/journal"
                  element={
                    <ProtectedRoute>
                      <TradeJournal />
                    </ProtectedRoute>
                  }
                />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<RoleBasedRedirect />} />
              <Route path="*" element={<RoleBasedRedirect />} />
            </Routes>
            <Box sx={{ textAlign: 'center', py: 2, bgcolor: 'grey.200', mt: 'auto' }}>
              <Typography variant="body2">
                Journaling Trader - 2025 | Built by ZenoLaunch
              </Typography>
            </Box>
          </Box>
          {/* PWA Install Prompt */}
          <InstallPrompt />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
